/**
 * FILE: src/blueprint/screens/Overview/BlueprintOverviewScreen.jsx
 * GOAL: Show pack overview and modules list; bootstrap config and pack endpoints.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getUserErrorMessage } from '../../../utils/errorMessages.js';
import { adaptBlueprint } from '../../adapters/blueprint/blueprintAdapter.js';
import {
  fetchBlueprintRunOverview,
  fetchClientConfig,
  fetchPackBlueprint,
  fetchPackModulesOverview,
  getServerBaseUrlFromConfig,
} from '../../api/clientApi.js';
import CoachSettings from '../../components/insights/CoachSettings.jsx';
import { ACTIONS } from '../../state/blueprintReducer.js';
import {
  selectBlueprint,
  selectModulePercent,
  selectModules,
  selectOverallProgressPercent,
} from '../../state/selectors.js';
import { useBlueprintRouter } from '../../state/useBlueprintRouter.js';
import { useBlueprintState } from '../../state/useBlueprintState.js';

function asString(value) {
  return value == null ? '' : String(value);
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function toTitleCase(value) {
  const text = asString(value).trim();
  if (!text) return '';

  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

function getSortedTracks(moduleItem) {
  const tracks = Array.isArray(moduleItem?.tracks) ? moduleItem.tracks : [];
  return tracks.slice().sort((a, b) => Number(a?.rank ?? 0) - Number(b?.rank ?? 0));
}

function getTrackActionLabel(track, moduleItem, percent) {
  const trackLabel = asString(track?.startLabel).trim() || asString(track?.label).trim() || '';

  if (trackLabel) {
    return trackLabel;
  }

  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  const hasProgress = pct > 0;

  if (hasProgress && pct < 100) {
    return 'Continue';
  }

  if (pct >= 100) {
    return 'Open';
  }

  return 'Start';
}

function getModuleDescription(moduleItem) {
  return asString(moduleItem?.subtitle).trim() || asString(moduleItem?.description).trim();
}

function getOverviewIntro(blueprint, modules) {
  const explicit =
    asString(blueprint?.overviewIntro).trim() ||
    asString(blueprint?.subtitle).trim() ||
    asString(blueprint?.description).trim();

  if (explicit) {
    return explicit;
  }

  const firstModule = modules[0];
  const firstTitle = toTitleCase(firstModule?.title || firstModule?.id);

  if (firstTitle) {
    return `Start your journey by exploring ${firstTitle}.`;
  }

  return 'Choose a module to begin your journey.';
}

function getOverviewHint(modules) {
  if (!Array.isArray(modules) || modules.length === 0) {
    return '';
  }

  return 'Select a module to gain insights on.';
}

function getModulePopover(moduleItem) {
  const progressPopover = asObject(moduleItem?.progress)?.popover;
  if (progressPopover) return progressPopover;

  const moduleProgressPopover = asObject(moduleItem?.moduleProgress)?.popover;
  if (moduleProgressPopover) return moduleProgressPopover;

  return null;
}

function getModulePopoverElements(moduleItem) {
  const popover = getModulePopover(moduleItem);
  return asArray(popover?.elements).filter(
    (item) => asObject(item) && asString(item?.value).trim()
  );
}

function getPrimaryMetrics(moduleItem, percent) {
  const popoverElements = getModulePopoverElements(moduleItem);

  if (popoverElements.length) {
    return popoverElements
      .filter((item) => !asString(item?.className).includes('is-muted'))
      .slice(0, 3)
      .map((item, index) => ({
        id: asString(item?.id, `metric_${index + 1}`),
        label: asString(item?.label).trim(),
        value: asString(item?.value).trim(),
        className: asString(item?.className).trim(),
      }));
  }

  const answeredCount = Number(moduleItem?.answered_count ?? moduleItem?.completedSteps ?? 0) || 0;
  const totalCount = Number(moduleItem?.total_count ?? moduleItem?.totalSteps ?? 0) || 0;
  const patternsIdentified =
    Number(moduleItem?.patterns_identified ?? moduleItem?.insightsCount ?? 0) || 0;
  const reportsAvailable =
    Number(moduleItem?.reports_available ?? moduleItem?.reportsCount ?? 0) || 0;

  return [
    {
      id: 'core_answers',
      label: 'Core answers',
      value: totalCount > 0 ? `${answeredCount}/${totalCount}` : `${Math.round(percent)}%`,
      className: '',
    },
    {
      id: 'patterns_identified',
      label: 'Patterns identified',
      value: String(patternsIdentified),
      className: '',
    },
    {
      id: 'reports_available',
      label: 'Reports available',
      value: String(reportsAvailable),
      className: '',
    },
  ];
}

function getModuleFooterNote(moduleItem) {
  const popoverElements = getModulePopoverElements(moduleItem);
  const mutedItem = popoverElements.find((item) => asString(item?.className).includes('is-muted'));

  if (mutedItem) {
    return asString(mutedItem.value).trim();
  }

  return '';
}

function getResolvedCoach(moduleItem) {
  const resolution = asObject(moduleItem?.coach_resolution);
  const active = asObject(resolution?.active);

  const coachId = asString(active?.id).trim();
  const coachLabel = asString(active?.label).trim() || coachId || 'System';

  return {
    id: coachId || 'system',
    label: coachLabel,
    fallbackUsed: Boolean(resolution?.fallback_used),
  };
}

function getOverviewCoachOptions(blueprint) {
  return asArray(blueprint?.available_coaches)
    .map((coach) => ({
      id: asString(coach?.id).trim(),
      label: asString(coach?.label).trim() || asString(coach?.id).trim(),
      persona: asObject(coach?.persona),
    }))
    .filter((coach) => coach.id);
}

function ModuleCard({ moduleItem, percent, onOpen, busy }) {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  const tracks = getSortedTracks(moduleItem);
  const defaultTrackId = moduleItem?.default_track_id || tracks[0]?.track_id || 'quick';
  const title = toTitleCase(moduleItem?.title || moduleItem?.id);
  const description = getModuleDescription(moduleItem);
  const answeredCount = Number(moduleItem?.answered_count ?? moduleItem?.completedSteps ?? 0) || 0;
  const totalCount = Number(moduleItem?.total_count ?? moduleItem?.totalSteps ?? 0) || 0;
  const metrics = getPrimaryMetrics(moduleItem, pct);
  const footerNote = getModuleFooterNote(moduleItem);
  const resolvedCoach = getResolvedCoach(moduleItem);

  const barStyle = useMemo(() => {
    return { '--dv-br-bar-pct': `${pct}%` };
  }, [pct]);

  return (
    <article className="dv-br-modCard">
      <div className="dv-br-modCard__inner">
        <header className="dv-br-modCard__header">
          <div className="dv-br-modCard__titleRow">
            <h3 className="dv-br-modCard__title">{title}</h3>
            <span className="dv-br-modCard__coachBadge">
              Coach: {resolvedCoach.label}
              {resolvedCoach.fallbackUsed ? ' (fallback)' : ''}
            </span>
          </div>

          {description ? <p className="dv-br-modCard__description">{description}</p> : null}

          <p className="dv-br-modCard__meta">
            {answeredCount}/{totalCount} steps
          </p>
        </header>

        <div className="dv-br-modCard__barRow" aria-label={`Module progress ${pct}%`}>
          <div className="dv-br-modCard__bar" style={barStyle}>
            <div className="dv-br-modCard__barFill" />
          </div>
          <div className="dv-br-modCard__pct">{pct}%</div>
        </div>

        {metrics.length ? (
          <div className="dv-br-modCard__stats" aria-label="Module progress details">
            {metrics.map((metric) => (
              <div key={metric.id} className="dv-br-modCard__statRow">
                <span className="dv-br-modCard__statLabel">{metric.label}</span>
                <strong className="dv-br-modCard__statValue">{metric.value}</strong>
              </div>
            ))}
          </div>
        ) : null}

        {footerNote ? <p className="dv-br-modCard__note">{footerNote}</p> : null}

        <div className="dv-br-modCard__actions">
          {tracks.map((track) => {
            const trackId = asString(track?.track_id).trim();
            const isDefault = trackId === asString(defaultTrackId).trim();
            const className = isDefault
              ? 'dv-br-btn dv-br-btn--primary'
              : 'dv-br-btn dv-br-btn--secondary';
            const actionLabel = getTrackActionLabel(track, moduleItem, pct);

            return (
              <button
                key={trackId || 'track'}
                type="button"
                className={className}
                onClick={() => onOpen(trackId || defaultTrackId)}
                disabled={busy}
                aria-label={`${actionLabel} ${title}${trackId ? ` (${trackId})` : ''}`}
              >
                {actionLabel}
              </button>
            );
          })}
        </div>
      </div>
    </article>
  );
}

export default function BlueprintOverviewScreen({ fromUrl = '' }) {
  const { state, dispatch, pilotConsentAccepted } = useBlueprintState();
  const { openModule } = useBlueprintRouter();

  const abortRef = useRef(null);
  const [isRailOpen, setIsRailOpen] = useState(true);

  const blueprint = selectBlueprint(state);
  const modules = selectModules(state);
  const overallPct = selectOverallProgressPercent(state);
  const selectedCoachId = asString(state?.selectedCoachId).trim();
  const selectedTypingMode = asString(state?.selectedTypingMode).trim() || 'fast';

  const isBusy = Boolean(state?.isLoading);
  const error = asString(state?.error).trim();

  const overviewIntro = useMemo(() => getOverviewIntro(blueprint, modules), [blueprint, modules]);
  const overviewHint = useMemo(() => getOverviewHint(modules), [modules]);
  const coachOptions = useMemo(() => getOverviewCoachOptions(blueprint), [blueprint]);

  const handleOpenModule = useCallback(
    (moduleId, trackId) => {
      if (!pilotConsentAccepted) {
        return;
      }

      const safeModuleId = asString(moduleId).trim();
      const safeTrackId = asString(trackId).trim();

      if (!safeModuleId || !safeTrackId) return;

      dispatch({ type: ACTIONS.SET_ERROR, payload: { error: '' } });
      dispatch({ type: ACTIONS.SET_ACTIVE_MODULE_DOC, payload: { moduleDoc: null } });

      openModule(safeModuleId, safeTrackId);
    },
    [dispatch, openModule, pilotConsentAccepted]
  );

  const handleCoachChange = useCallback(
    (event) => {
      dispatch({
        type: ACTIONS.SET_SELECTED_COACH,
        payload: {
          coachId: asString(event?.target?.value).trim(),
        },
      });
    },
    [dispatch]
  );

  const handleTypingModeChange = useCallback(
    (event) => {
      dispatch({
        type: ACTIONS.SET_SELECTED_TYPING_MODE,
        payload: {
          typingMode: asString(event?.target?.value).trim(),
        },
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!pilotConsentAccepted) {
      abortRef.current?.abort?.();
      dispatch({ type: ACTIONS.SET_LOADING, payload: { isLoading: false } });
      return;
    }

    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      dispatch({ type: ACTIONS.SET_ERROR, payload: { error: '' } });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { isLoading: true } });

      try {
        const cfg = await fetchClientConfig({ signal: controller.signal });
        dispatch({ type: ACTIONS.SET_CONFIG, payload: { config: cfg } });

        const serverBaseUrl = getServerBaseUrlFromConfig(cfg);
        const packId = asString(state?.packId).trim();

        if (!serverBaseUrl) {
          throw new Error('Missing server_base_url from config.');
        }

        if (!packId) {
          throw new Error('Missing pack_id.');
        }

        const [bp, ov, runOverviewRaw] = await Promise.all([
          fetchPackBlueprint({
            serverBaseUrl,
            packId,
            signal: controller.signal,
          }),
          fetchPackModulesOverview({
            serverBaseUrl,
            packId,
            signal: controller.signal,
          }),
          fetchBlueprintRunOverview({
            payload: {
              pack_id: packId,
              assistant: {
                id: selectedCoachId,
              },
              typing_mode: selectedTypingMode,
            },
            signal: controller.signal,
          }),
        ]);

        const runOverview = runOverviewRaw || {
          pack_id: packId,
          modules: [],
          overall: {
            progress_ratio: 0,
            answered_count: 0,
            total_count: 0,
          },
        };

        console.log('raw overview', runOverview);

        const adapted = adaptBlueprint({
          packId,
          blueprint: bp,
          modulesOverview: ov,
          runOverview,
        });

        console.log('adapted overview', adapted);

        dispatch({ type: ACTIONS.SET_BLUEPRINT, payload: { blueprint: adapted } });
      } catch (e) {
        if (e?.name === 'AbortError') return;

        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: { error: getUserErrorMessage(e) },
        });
      } finally {
        dispatch({ type: ACTIONS.SET_LOADING, payload: { isLoading: false } });
      }
    })();

    return () => controller.abort();
  }, [dispatch, state?.packId, selectedCoachId, selectedTypingMode, pilotConsentAccepted]);

  return (
    <div className="dv-br-shell">
      <header className="dv-br-topbar">
        <div className="dv-br-topbar__left">
          {fromUrl ? (
            <a href={fromUrl} className="dv-br-back__link" aria-label="Back">
              ← Back
            </a>
          ) : null}
        </div>

        <div className="dv-br-topbar__title">{blueprint?.title ?? 'Blueprint'}</div>

        <div className="dv-br-topbar__right">
          <div className="dv-br-topbar__progressLabel">progress</div>
          <div
            className="dv-br-ring"
            style={{ '--dv-br-ring-pct': `${overallPct}%` }}
            aria-label={`Progress ${overallPct}%`}
          >
            <div className="dv-br-ring__value">{overallPct}%</div>
          </div>
        </div>
      </header>

      <main className="dv-br-main">
        <div
          className={`dv-br-overview ${isRailOpen ? 'dv-br-overview--rail-open' : 'dv-br-overview--rail-closed'}`}
        >
          <section className="dv-br-overview__main" aria-label="Overview main">
            {error ? <p className="dv-br-overview__hint">⚠ {error}</p> : null}
            {isBusy ? <p className="dv-br-overview__hint">Loading…</p> : null}

            {!isBusy && !error && modules.length === 0 ? (
              <p className="dv-br-overview__hint">No modules found.</p>
            ) : null}

            {!error && !isBusy ? (
              <>
                {overviewIntro ? <p className="dv-br-overview__hint">{overviewIntro}</p> : null}
                {overviewHint ? <p className="dv-br-overview__hint">{overviewHint}</p> : null}
              </>
            ) : null}

            <div className="dv-br-modGrid" aria-label="Modules">
              {modules.map((moduleItem) => (
                <ModuleCard
                  key={moduleItem.id}
                  moduleItem={moduleItem}
                  percent={selectModulePercent(moduleItem)}
                  busy={isBusy}
                  onOpen={(trackId) => handleOpenModule(moduleItem.id, trackId)}
                />
              ))}
            </div>
          </section>

          <aside className="dv-br-module__side" aria-label="Overview side panel">
            {isRailOpen ? (
              <>
                <button
                  type="button"
                  className="dv-br-module__sideClose"
                  onClick={() => setIsRailOpen(false)}
                  aria-expanded={true}
                  aria-label="Close Guided Support"
                >
                  ×
                </button>

                <div className="dv-br-module__sideInner">
                  <div className="dv-br-module__sideHeader">
                    <h3 className="dv-br-module__sideTitle">Guided Support</h3>
                  </div>

                  <CoachSettings
                    coachOptions={coachOptions}
                    selectedCoachId={selectedCoachId}
                    handleCoachChange={handleCoachChange}
                    isBusy={isBusy}
                    selectedTypingMode={selectedTypingMode}
                    handleTypingModeChange={handleTypingModeChange}
                  />

                  {error ? <p className="dv-br-module__sideText">⚠ {error}</p> : null}
                </div>
              </>
            ) : (
              <button
                type="button"
                className="dv-br-module__sideToggle dv-br-module__sideToggle--collapsed"
                onClick={() => setIsRailOpen(true)}
                aria-expanded={false}
                aria-label="Open Guided Support"
              >
                Guided Support
              </button>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
